import { Controller } from "@hotwired/stimulus"
import { Room, RoomEvent } from "livekit-client"

/**
 * Base controller for LiveKit functionality
 * Provides shared methods for connection, token fetching, and microphone publishing
 */
export class LivekitBaseController extends Controller {
  static values = {
    chatId: Number,
    livekitWsUrl: String
  }

  // ========================================
  // LIVEKIT TOKEN MANAGEMENT
  // ========================================

  /**
   * Fetch LiveKit token from Rails backend
   * @param {Object} metadata - Metadata to include in room
   * @returns {Promise<Object>} Token data from server
   */
  async fetchLiveKitToken(metadata = {}) {
    const response = await fetch('/agent/livekit/token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': this.getCsrfToken()
      },
      body: JSON.stringify({
        room_name: this.generateRoomName(),
        participant_name: this.generateParticipantName(),
        metadata: metadata
      })
    })

    if (!response.ok) {
      throw new Error('Failed to get LiveKit token')
    }

    return await response.json()
  }

  // ========================================
  // ROOM CONNECTION
  // ========================================

  /**
   * Connect to LiveKit room
   * @param {Object} tokenData - Token data from fetchLiveKitToken
   * @param {Object} eventHandlers - Event handlers for room events
   * @returns {Promise<Room>} Connected room instance
   */
  async connectToRoom(tokenData, eventHandlers = {}) {
    this.room = new Room({
      adaptiveStream: true,
      dynacast: true,
    })

    // Setup event handlers
    this.room.on(RoomEvent.Connected, eventHandlers.onConnected || (() => {
      console.log(`[${this.identifier}] Connected to room`)
    }))

    this.room.on(RoomEvent.Disconnected, eventHandlers.onDisconnected || (() => {
      console.log(`[${this.identifier}] Disconnected from room`)
    }))

    this.room.on(RoomEvent.Reconnecting, eventHandlers.onReconnecting || (() => {
      console.log(`[${this.identifier}] Reconnecting...`)
    }))

    if (eventHandlers.onDataReceived) {
      this.room.on(RoomEvent.DataReceived, eventHandlers.onDataReceived)
    }

    if (eventHandlers.onTrackSubscribed) {
      this.room.on(RoomEvent.TrackSubscribed, eventHandlers.onTrackSubscribed)
    }

    // Connect to room using Stimulus value for WS URL
    await this.room.connect(this.livekitWsUrlValue, tokenData.token)
    console.log(`[${this.identifier}] Room connected:`, this.room.name)

    return this.room
  }

  /**
   * Disconnect from LiveKit room
   */
  disconnectFromRoom() {
    if (this.room) {
      this.room.disconnect()
      this.room = null
    }
  }

  // ========================================
  // MICROPHONE MANAGEMENT
  // ========================================

  /**
   * Publish microphone to room
   * @param {Object} options - Audio constraints options
   */
  async publishMicrophone(options = {}) {
    const defaultOptions = {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    }

    await this.room.localParticipant.setMicrophoneEnabled(true, {
      ...defaultOptions,
      ...options
    })

    console.log(`[${this.identifier}] Microphone published`)
  }

  /**
   * Stop publishing microphone
   */
  stopPublishing() {
    if (this.room) {
      this.room.localParticipant.setMicrophoneEnabled(false)
    }
  }

  // ========================================
  // HELPER METHODS
  // ========================================

  /**
   * Get CSRF token from page meta tag
   * @returns {string} CSRF token
   */
  getCsrfToken() {
    const token = document.querySelector('meta[name="csrf-token"]')
    return token ? token.content : ''
  }

  /**
   * Generate unique room name
   * @returns {string} Room name
   */
  generateRoomName() {
    return `${this.identifier}-${this.chatIdValue}-${Date.now()}`
  }

  /**
   * Generate participant name
   * @returns {string} Participant name
   */
  generateParticipantName() {
    return `user-${this.chatIdValue}`
  }

  /**
   * Escape HTML for safe display
   * @param {string} text - Text to escape
   * @returns {string} Escaped HTML
   */
  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}
